import { ref, computed } from '@vue/composition-api'
import { findIndex } from 'lodash'
import UserApi from '@/api/v2/employee/user-management/User'
import { handleApiError } from '@/utils/errorHandler'

export default function useUsers(vm, currentRole) {
  const users = ref([])
  const selectedUser = ref(null)
  const newUserDialog = ref(false)
  const editDialog = ref(false)

  const filteredUsers = computed(() => {
    return users.value.filter(user => user.attributes.role === currentRole.value)
  })

  const getUsers = async () => {
    const res = await UserApi.getUsers()

    if (res.data.status === 'success') {
      users.value = res.data.data.data
    }
  }

  const createUser = async ({
    name,
    profileImage,
    userSystemFlaggingsAttributes = [],
    userCustomFieldValuesAttributes = [],
    userAggregationTemplateId,
    aggregationRole,
    callback,
  }) => {
    try {
      const res = await UserApi.createUser({
        name,
        role: currentRole.value,
        profileImage,
        userSystemFlaggingsAttributes,
        userCustomFieldValuesAttributes,
        userAggregationTemplateId,
        aggregationRole,
      })
      if (res.data.status === 'success') {
        users.value = [res.data.data.data, ...users.value]
        vm.$toast.success('ユーザーを新規作成しました')

        return true
      }

      if (res.data.status === 'error') {
        vm.$toast.error('ユーザーの新規作成に失敗しました')
        vm.$toast.error(res.data.message.join('\n'))

        return false
      }

      return false
    } catch (error) {
      handleApiError(error, { $toast: vm.$toast })

      return false
    } finally {
      callback()
    }
  }

  const updateUser = async ({
    id,
    name,
    profileImage,
    userSystemFlaggingsAttributes = [],
    userCustomFieldValuesAttributes = [],
    userAggregationTemplateId,
    aggregations = [],
    aggregationRole,
    retiredAt,
    callback,
  }) => {
    try {
      const res = await UserApi.updateUser({
        id,
        name,
        profileImage,
        userSystemFlaggingsAttributes,
        userCustomFieldValuesAttributes,
        userAggregationTemplateId,
        aggregations,
        aggregationRole,
        retiredAt,
      })

      if (res.data.status === 'success') {
        const updatedUser = res.data.data.data
        const userIdx = findIndex(users.value, user => Number(user.id) === Number(updatedUser.id))
        users.value.splice(userIdx, 1, updatedUser)
        vm.$toast.success('ユーザーを更新しました')

        return true
      }
      if (res.data.status === 'error') {
        vm.$toast.error('ユーザーの更新に失敗しました')
        vm.$toast.error(res.data.message.join('\n'))

        return false
      }

      return false
    } catch (error) {
      handleApiError(error, { $toast: vm.$toast })

      return false
    } finally {
      callback()
    }
  }

  // カスタムフィールド1件のインライン保存(他の属性は送らない)
  const saveCustomFieldValue = async ({ id, userCustomFieldValuesAttributes, fieldName }) => {
    try {
      const res = await UserApi.updateUser({ id, userCustomFieldValuesAttributes })

      if (res.data.status === 'success') {
        const updatedUser = res.data.data.data
        const userIdx = findIndex(users.value, user => Number(user.id) === Number(updatedUser.id))
        users.value.splice(userIdx, 1, updatedUser)
        vm.$toast.success(`「${fieldName}」を保存しました`)

        return true
      }
      if (res.data.status === 'error') {
        vm.$toast.error(`「${fieldName}」の保存に失敗しました`)
        vm.$toast.error(res.data.message.join('\n'))

        return false
      }

      return false
    } catch (error) {
      handleApiError(error, { $toast: vm.$toast })

      return false
    }
  }

  // 選択したユーザーをまとめて退店/退店解除する(退店日時のみを1人ずつ更新)
  const bulkUpdateRetired = async ({ ids, retire }) => {
    const retiredAt = retire ? new Date().toISOString() : null
    let successCount = 0

    /* eslint-disable no-await-in-loop */
    for (let i = 0; i < ids.length; i += 1) {
      try {
        const res = await UserApi.updateUser({ id: ids[i], retiredAt })

        if (res.data.status === 'success') {
          const updatedUser = res.data.data.data
          const userIdx = findIndex(users.value, user => Number(user.id) === Number(updatedUser.id))
          users.value.splice(userIdx, 1, updatedUser)
          successCount += 1
        }
      } catch (error) {
        handleApiError(error, { $toast: vm.$toast })
      }
    }
    /* eslint-enable no-await-in-loop */

    if (successCount > 0) {
      vm.$toast.success(retire ? `${successCount}人を退店にしました` : `${successCount}人の退店を解除しました`)
    }
    if (successCount < ids.length) {
      vm.$toast.error(`${ids.length - successCount}人の更新に失敗しました`)
    }

    return successCount === ids.length
  }

  // 選択したユーザーへ集計テンプレートを一括適用。
  // 編集ダイアログでテンプレートを選んで保存したときと同じ置き換え動作
  // (各自の既存割当を削除対象として送り、テンプレートの集計表へ割り当てる)
  const bulkApplyTemplate = async ({ targets, templateId, templateName }) => {
    let successCount = 0

    /* eslint-disable no-await-in-loop */
    for (let i = 0; i < targets.length; i += 1) {
      try {
        const res = await UserApi.updateUser({
          id: targets[i].id,
          userAggregationTemplateId: templateId,
          aggregations: targets[i].aggregations,
        })

        if (res.data.status === 'success') {
          const updatedUser = res.data.data.data
          const userIdx = findIndex(users.value, user => Number(user.id) === Number(updatedUser.id))
          users.value.splice(userIdx, 1, updatedUser)
          successCount += 1
        }
      } catch (error) {
        handleApiError(error, { $toast: vm.$toast })
      }
    }
    /* eslint-enable no-await-in-loop */

    if (successCount > 0) {
      vm.$toast.success(`${successCount}人に「${templateName}」を適用しました`)
    }
    if (successCount < targets.length) {
      vm.$toast.error(`${targets.length - successCount}人の適用に失敗しました`)
    }

    return successCount === targets.length
  }

  const deleteUser = async ({ id, callback }) => {
    try {
      const res = await UserApi.deleteUser(id)

      if (res.data.status === 'success') {
        const userIdx = findIndex(users.value, user => Number(user.id) === Number(id))
        users.value.splice(userIdx, 1)
        vm.$toast.success('ユーザーを削除しました')

        return true
      }

      if (res.data.status === 'error') {
        vm.$toast.error('ユーザーの削除に失敗しました')
        vm.$toast.error(res.data.message.join('\n'))

        return false
      }

      return false
    } catch (error) {
      handleApiError(error, { $toast: vm.$toast })

      return false
    } finally {
      callback()
    }
  }

  const openNewUserDialog = () => {
    newUserDialog.value = true
  }

  const openEditDialog = user => {
    selectedUser.value = user
    editDialog.value = true
  }

  return {
    users,
    selectedUser,
    newUserDialog,
    editDialog,
    filteredUsers,
    getUsers,
    createUser,
    updateUser,
    saveCustomFieldValue,
    bulkUpdateRetired,
    bulkApplyTemplate,
    deleteUser,
    openNewUserDialog,
    openEditDialog,
  }
}
